import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import {
  MatDialog,
  MatDialogConfig,
  MatDialogRef,
  MAT_DIALOG_DATA
} from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslocoService } from '@ngneat/transloco';
import { ApolloError } from 'apollo-client';
import { Logger } from 'aws-amplify';
import { Observable, Subscription } from 'rxjs';
import {
  CreateQuestionnaireRequestInput,
  MassClinicPatient,
  NameFormat,
  QuestionnaireType
} from 'src/API';
import { ErrorHandlerService } from 'src/app/core/api/error-handler.service';
import { safeJsonParse } from 'src/app/core/api/general.util';
import { QuestionnaireRequestUtils } from 'src/app/core/api/questionnaire-request-utils';
import {
  QuestionnaireRequest,
  QuestionnaireRequestService,
  SendQuestionnaireDialogData
} from 'src/app/core/api/questionnaire-request.service';
import { StaffService } from 'src/app/core/api/staff.service';
import { IntakeFormService } from 'src/app/intake-form/intake-form.service';
import { v4 as uuid } from 'uuid';
import { Patient, PatientService } from '../../core/api/patient.service';
import {
  CheckboxAndInputComponent,
  CheckboxAndInputError
} from '../checkbox-and-input/checkbox-and-input.component';
import { UpdateMassClinicPatientInput } from './../../../API';
import { ClinicSetupService } from './../../logged-in-navbar/clinic-setup-modal/clinic-setup.service';

export type SendQuestionnaireMatDialogRef = MatDialogRef<SendQuestionnaireDialogComponent>;

@Component({
  selector: 'csi-send-questionnaire-dialog',
  templateUrl: './send-questionnaire-dialog.component.html',
  styleUrls: ['./send-questionnaire-dialog.component.css']
})
export class SendQuestionnaireDialogComponent implements OnInit {
  readonly NameFormat = NameFormat;
  public formGroup: FormGroup;
  public questionnaireTypesFormGroup: FormGroup;
  public sendClicked = false;
  public sending = false;
  public atLeastOneCheckboxSelected = false;
  public questionnairesToBeSent = [];
  public canSendAsMultiQuestionnaire = [
    'SPEED',
    'SPEEDII',
    'OSDI',
    'DEQ',
    'FITZPATRICK',
    'OSDI6',
    'MEDICATION'
  ];
  private translationSub: Subscription;
  private questionnaireRequestsSent = [];
  public questionnaireTypeToFriendlyNameMap = {};
  public isToggle: Boolean = false;

  public languageFormControl = new FormControl();

  private clinicReminderSchedule = null;
  private questionnaireExpiryDate = this.clinicSetupService.clinicSetupFormGroup.value
    .questionnaireExpiry;

  readonly emailErrors: CheckboxAndInputError[] = [
    { errorType: 'email', displayMessage: 'Invalid Email Address' },
    { errorType: 'required', displayMessage: 'Email is required' }
  ];

  readonly mobileErrors: CheckboxAndInputError[] = [
    { errorType: 'required', displayMessage: 'Mobile phone is required' }
  ];

  @ViewChild('emailInputs', { static: true }) emailInputs: CheckboxAndInputComponent;
  @ViewChild('mobileInputs', { static: true }) mobileInputs: CheckboxAndInputComponent;

  private logger: Logger = new Logger('SendQuestionnaireDialogComponent');

  public intakeFormKeyToFriendlyNameMap = {};
  public intakeFormKeyToSchemaMap = {};
  public sendIntakeFormsEnabled = true;

  public multiSendDisabled = false;

  constructor(
    public dialogRef: MatDialogRef<SendQuestionnaireDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: SendQuestionnaireDialogData,
    public questionnaireRequestService: QuestionnaireRequestService,
    private errorHandlerService: ErrorHandlerService,
    private snackBar: MatSnackBar,
    public translocoService: TranslocoService,
    public clinicSetupService: ClinicSetupService,
    public staffService: StaffService,
    public intakeFormService: IntakeFormService,
    public patientService: PatientService
  ) {
    this.formGroup = this.questionnaireRequestService.createFormGroup(data);
    if (data.multipleQuestionnairesSelectable) {
      this.questionnaireTypesFormGroup = new FormGroup({});
    }
    const languageToSend =
      (this.staffService.staff && this.staffService.staff.preferredLanguage) ||
      this.translocoService.getDefaultLang();

    this.languageFormControl.setValue(languageToSend);

    const { CUSTOM, ...rest } = this.questionnaireRequestService.questionnaireTypeToFriendlyNameMap;
    this.questionnaireTypeToFriendlyNameMap = rest;
  }

  ngOnDestroy() {
    if (this.translationSub) {
      this.translationSub.unsubscribe();
    }
  }

  public static open(
    matDialogService: MatDialog,
    questionnaireType: QuestionnaireType,
    patient: Partial<Patient>,
    modalConfig: MatDialogConfig<never> = {},
    massClinicPatient: MassClinicPatient = null,
    multipleQuestionnairesSelectable = false
  ): SendQuestionnaireMatDialogRef {
    return matDialogService.open<SendQuestionnaireDialogComponent, SendQuestionnaireDialogData>(
      SendQuestionnaireDialogComponent,
      {
        maxWidth: '95vw',
        height: 'auto',
        ...modalConfig,
        data: {
          questionnaireType,
          patient,
          massClinicPatient,
          questionnaireRequest:
            massClinicPatient && (massClinicPatient.questionnaireRequest as QuestionnaireRequest),
          multipleQuestionnairesSelectable
        }
      }
    );
  }

  ngOnInit() {
    if (this.mobileInputs) {
      this.mobileInputs.checkboxControl.setValue(false); // Text message should be false by default
    }
    if (this.emailInputs) {
      this.emailInputs.checkboxControl.setValue(!!this.emailFormControl.value);
    }
    this.initializeIntakeFormMaps();
    this.sendIntakeFormsEnabled = this.shouldEnableSendingIntakeForms();
  }

  onQuestionnaireSelectChange(atLeastOneCheckboxSelected: boolean) {
    this.atLeastOneCheckboxSelected = atLeastOneCheckboxSelected;
    const controls = this.questionnaireTypesFormGroup.controls;
    const { OSDI, SPEED, OSDIAndSPEED, SPEEDII, OSDI6, standard, DERFS, CDERFS } = controls;

    this.multiSendDisabled = false;

    [OSDI, OSDI6, SPEED, OSDIAndSPEED, SPEEDII, DERFS, CDERFS].forEach(c => c.enable());
    if (this.sendIntakeFormsEnabled) standard.enable();

    const exclusiveGroups = [
      {
        condition: [OSDIAndSPEED],
        disabled: [OSDI, OSDI6, SPEED, SPEEDII]
      },
      { condition: [OSDI, SPEED], disabled: [OSDIAndSPEED] },
      { condition: [SPEEDII], disabled: [SPEED] },
      { condition: [SPEED], disabled: [SPEEDII] },
      { condition: [OSDI], disabled: [OSDI6] },
      { condition: [OSDI6], disabled: [OSDI] },
      { condition: [DERFS], disabled: [CDERFS] },
      { condition: [CDERFS], disabled: [DERFS] }
    ];
    //Kostya was here

    exclusiveGroups.forEach(group => {
      if (group.condition.some(c => c.value)) {
        this.multiSendDisabled = true;
        group.disabled.forEach(c => {
          c.disable();
          c.setValue(false);
        });
      }
    });

    if (this.sendIntakeFormsEnabled) {
      if (standard.value) {
        this.multiSendDisabled = true;
        [OSDI, OSDI6, SPEED, SPEEDII, OSDIAndSPEED].forEach(c => {
          c.disable();
          c.setValue(false);
        });
      }
    }
  }

  onSubmit() {
    if (this.sending || this.formGroup.invalid) {
      return;
    }
    this.sendClicked = true;
    this.sending = true;

    if (this.data.multipleQuestionnairesSelectable && this.atLeastOneCheckboxSelected) {
      Object.keys({
        ...this.questionnaireRequestService.questionnaireTypeToFriendlyNameMap,
        ...this.questionnaireRequestService.customQuestionnaireKeyToFriendlyNameMap,
        ...(this.sendIntakeFormsEnabled ? this.intakeFormKeyToFriendlyNameMap : [])
      }).forEach(key => {
        if (
          this.questionnaireTypesFormGroup.controls[key] &&
          this.questionnaireTypesFormGroup.controls[key].value === true
        ) {
          this.questionnairesToBeSent.push(key);
        }
      });

      if (!this.data.questionnaireRequest && this.questionnairesToBeSent.length !== 0) {
        const questionnaireGroupId = uuid();
        this.createQuestionnaireRequest(
          this.questionnairesToBeSent.pop(),
          this.questionnairesToBeSent.length > 0 ? questionnaireGroupId : undefined
        );
        return;
      }
      this.updateQuestionnaireType(this.questionnairesToBeSent.pop());
    } else if (!this.data.multipleQuestionnairesSelectable) {
      if (!this.data.questionnaireRequest) {
        this.createQuestionnaireRequest();
        return;
      }
      this.updateQuestionnaireType();
    } else {
      this.sending = false;
    }
  }

  public get mobileFormControl(): AbstractControl {
    return this.formGroup.controls.mobile;
  }

  public get emailFormControl(): AbstractControl {
    return this.formGroup.controls.email;
  }

  initializeIntakeFormMaps() {
    this.intakeFormKeyToSchemaMap = Object.entries({
      ...this.intakeFormService.defaultIntakeFormTemplateSchemas,
      ...this.questionnaireRequestService.customIntakeFormKeyToSchemaMap
    }).reduce((acc, [key, value]) => {
      acc[key] = value['schema'] ? value['schema'] : value;
      return acc;
    }, {});

    this.intakeFormKeyToFriendlyNameMap = Object.entries(this.intakeFormKeyToSchemaMap).reduce(
      (acc, [key, val]) => {
        acc[key] = val['name'];
        return acc;
      },
      {}
    );
  }

  shouldEnableSendingIntakeForms() {
    if (this.patientService.intakeForms && this.patientService.intakeForms.length > 0) {
      return false;
    }
    const intakeQuestionnaires = this.patientService.questionnaires.filter(questionnaire => {
      const config = safeJsonParse(questionnaire.config);
      return config && config.schema && config.schema.type === 'intake';
    });
    return intakeQuestionnaires.every(questionnaire =>
      this.questionnaireRequestService.isQuestionnaireExpired(questionnaire)
    );
  }

  private createQuestionnaireRequest(questionnaireType?: string, groupId?: string) {
    this.sending = true;
    if (questionnaireType) {
      this.formGroup.controls['type'].patchValue(questionnaireType);
    }
    const questionnaireRequestInput: CreateQuestionnaireRequestInput = {
      ...this.formGroup.value,
      language: this.languageFormControl.value,
      config: {
        questionnaireExpiry: this.clinicSetupService.clinicSetupFormGroup.value.questionnaireExpiry
      },
      ...(this.canSendAsMultiQuestionnaire.includes(questionnaireType) && { groupId })
    };

    const schema = {
      ...this.questionnaireRequestService.allQuestionnairesAndIntakeFormsKeyToSchemaMap,
      ...this.intakeFormKeyToSchemaMap
    }[questionnaireType];

    if (schema) {
      questionnaireRequestInput.type = this.questionnaireRequestService
        .formTemplateLibraryQuestionnaires[questionnaireType]
        ? (questionnaireType as QuestionnaireType)
        : QuestionnaireType.CUSTOM;

      questionnaireRequestInput.config = {
        ...(questionnaireRequestInput.config as any),
        schema: schema
      };
    }

    questionnaireRequestInput.config = JSON.stringify(questionnaireRequestInput.config as any);
    let questionnaireRequest$: Observable<QuestionnaireRequest>;

    if (this.data.massClinicPatient) {
      const questionnaireId = uuid();
      const { id, massClinicPatientClinicId, ...massClinicPatient } = this.data.massClinicPatient;

      const updateMassClinicPatient: UpdateMassClinicPatientInput = {
        id,
        massClinicPatientClinicId,
        massClinicPatientMassClinicId: massClinicPatient.massClinic.id,
        massClinicPatientPatientId: massClinicPatient.patient.id,
        massClinicPatientQuestionnaireRequestId: questionnaireId
      };

      questionnaireRequest$ = this.questionnaireRequestService.createQuestionnaireRequestAndUpdateMassClinicPatient(
        {
          id: questionnaireId,
          questionnaireRequestMassClinicPatientId: id,
          ...questionnaireRequestInput
        },
        updateMassClinicPatient
      );
    } else {
      questionnaireRequest$ = this.questionnaireRequestService.createQuestionnaireRequest(
        questionnaireRequestInput
      );
    }

    questionnaireRequest$.subscribe(
      (questionnaireRequest: QuestionnaireRequest) => {
        this.logger.debug('Submitted Questionnaire Request: ', questionnaireRequest);
        this.questionnaireRequestsSent.push(questionnaireRequest);
        this.patientService.questionnaires = [...this.patientService.questionnaires]; // Trigger change detection
        if (this.questionnairesToBeSent.length === 0) {
          this.dialogRef.close(
            this.questionnaireRequestsSent.length > 1
              ? this.questionnaireRequestsSent
              : this.questionnaireRequestsSent[0]
          );
          this.openSuccessSnackbar();
        } else {
          this.createQuestionnaireRequest(this.questionnairesToBeSent.pop(), groupId);
        }
      },
      (error: ApolloError) => {
        this.sending = false;
        this.errorHandlerService.handleGraphQLError(error);
      }
    );
  }

  public updateQuestionnaireType(questionnaireType?: string) {
    this.sending = true;
    if (questionnaireType) {
      this.formGroup.controls['type'].patchValue(questionnaireType);
    }
    this.questionnaireRequestService
      .updateQuestionnaireType(this.data.questionnaireRequest, this.formGroup.controls.type.value)
      .subscribe(
        (questionnaireRequest: QuestionnaireRequest) => {
          this.questionnaireRequestsSent.push(questionnaireRequest);
          if (this.questionnairesToBeSent.length === 0) {
            this.dialogRef.close(
              this.questionnaireRequestsSent.length > 1
                ? this.questionnaireRequestsSent
                : this.questionnaireRequestsSent[0]
            );
            this.openSuccessSnackbar();
          } else {
            this.createQuestionnaireRequest(this.questionnairesToBeSent.pop());
          }
        },
        (error: ApolloError) => {
          this.sending = false;
          this.errorHandlerService.handleGraphQLError(error);
        }
      );
  }

  private openSuccessSnackbar() {
    let dismissText = 'Dismiss';
    this.translationSub = this.translocoService.selectTranslate('Dismiss').subscribe(text => {
      dismissText = text || dismissText;
    });

    if (this.questionnaireRequestsSent.length > 1) {
      let message = 'The following questionnaires have been sent:';
      this.translationSub = this.translocoService
        .selectTranslate('The following questionnaires have been sent:')
        .subscribe(text => {
          message = text || message;
        });
      this.questionnaireRequestsSent.forEach(questionnaireRequest => {
        message = `${message} [${this.getQuestionnaireTypeFriendlyName(questionnaireRequest)}]`;
      });

      this.snackBar.open(message, dismissText, {
        duration: 7000
      });
    } else {
      const questionnaireType: string = this.getQuestionnaireTypeFriendlyName(
        this.questionnaireRequestsSent[0]
      );
      this.snackBar.open(`Sent ${questionnaireType}`, dismissText, {
        duration: 4000
      });
    }
  }

  private getQuestionnaireTypeFriendlyName(questionnaireRequest: QuestionnaireRequest) {
    if (questionnaireRequest.type === QuestionnaireType.CUSTOM) {
      const config = safeJsonParse(questionnaireRequest.config);
      if (config.schema && config.schema.name) {
        return config.schema.name;
      }
      return 'CUSTOM';
    }
    return QuestionnaireRequestUtils.getQuestionnaireType(questionnaireRequest.type);
  }
}
